import { useScienceTutorSection } from './scienceTutorContext';
import { CheckCircle2, Flame, Scale, XCircle } from 'lucide-react';
import { useState } from 'react';

import {
  CONSTANT_MASS_TOLERANCE_G,
  GRAVIMETRIC_PROBLEMS,
  MAX_WEIGHINGS,
  RATIO_OPTIONS,
  buildGravimetricModel,
  checkErrorEffect,
  checkGravimetricAnswer,
  checkRatioPrediction,
  formatMass,
  formatPercent,
  isConstantMass,
  ratioLabel,
  type ErrorEffect,
  type GravimetricErrorSource,
  type GravimetricModel,
  type GravimetricProblem,
} from './instrumentalGravimetric';
import { InstrumentalGravimetricExplore } from './InstrumentalGravimetricExplore';

const CHART_WIDTH = 320;
const CHART_HEIGHT = 180;
const CHART_PAD_LEFT = 52;
const CHART_PAD_BOTTOM = 28;
const CHART_PAD_TOP = 14;
const CHART_PAD_RIGHT = 12;
const BAR_GAP_FRACTION = 0.35;
/** Bars start just under the dry mass so the shrinking water excess is visible. */
const CHART_FLOOR_FRACTION = 0.9;

interface Feedback {
  correct: boolean;
  text: string;
}

function FeedbackPanel({ feedback, label, correctTitle, wrongTitle }: {
  feedback: Feedback;
  label: string;
  correctTitle: string;
  wrongTitle: string;
}) {
  return (
    <div
      role="status"
      aria-label={label}
      className={`mt-4 rounded-2xl border p-4 ${
        feedback.correct ? 'border-brand-mint/45 bg-wash-mint' : 'border-brand-coral/45 bg-wash-coral'
      }`}
    >
      <p className="flex items-center gap-2 font-extrabold text-ink">
        {feedback.correct ? (
          <CheckCircle2 className="h-5 w-5 text-brand-mint" aria-hidden="true" />
        ) : (
          <XCircle className="h-5 w-5 text-brand-coral" aria-hidden="true" />
        )}
        {feedback.correct ? correctTitle : wrongTitle}
      </p>
      <p className="mt-2 text-sm font-medium leading-6 text-ink-soft">{feedback.text}</p>
    </div>
  );
}

function WeighingChart({ problem, model, count }: {
  problem: GravimetricProblem;
  model: GravimetricModel;
  count: number;
}) {
  const shown = model.weighings.slice(0, count);
  const floor = problem.emptyCrucibleG + model.precipitateMassG * CHART_FLOOR_FRACTION;
  const top = model.weighings[0] + (model.weighings[0] - floor) * 0.1;
  const plotW = CHART_WIDTH - CHART_PAD_LEFT - CHART_PAD_RIGHT;
  const plotH = CHART_HEIGHT - CHART_PAD_TOP - CHART_PAD_BOTTOM;
  const slot = plotW / MAX_WEIGHINGS;
  const barW = slot * (1 - BAR_GAP_FRACTION);
  const y = (mass: number) => CHART_PAD_TOP + plotH - ((mass - floor) / (top - floor)) * plotH;
  const baseY = CHART_PAD_TOP + plotH;
  const dryY = y(problem.emptyCrucibleG + model.precipitateMassG);

  return (
    <svg
      viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
      className="block h-auto w-full max-w-lg"
      role="img"
      aria-label={`Weighings of crucible plus precipitate: ${
        shown.length > 0 ? shown.map((mass) => `${formatMass(mass)} g`).join(', ') : 'none yet'
      }`}
    >
      <line x1={CHART_PAD_LEFT} x2={CHART_WIDTH - CHART_PAD_RIGHT} y1={baseY} y2={baseY} className="stroke-ink" strokeWidth={1.5} />
      <line x1={CHART_PAD_LEFT} x2={CHART_PAD_LEFT} y1={CHART_PAD_TOP} y2={baseY} className="stroke-ink" strokeWidth={1.5} />
      <text x={CHART_PAD_LEFT - 4} y={baseY} textAnchor="end" className="fill-slate2 text-[8px] font-bold">
        {floor.toFixed(3)}
      </text>
      <text x={CHART_PAD_LEFT - 4} y={CHART_PAD_TOP + 6} textAnchor="end" className="fill-slate2 text-[8px] font-bold">
        {top.toFixed(3)}
      </text>
      <text x={10} y={CHART_PAD_TOP + plotH / 2} textAnchor="middle" transform={`rotate(-90 10 ${CHART_PAD_TOP + plotH / 2})`} className="fill-ink text-[9px] font-bold">
        Mass (g)
      </text>
      {count >= model.constantAtIndex + 1 ? (
        <g>
          <line x1={CHART_PAD_LEFT} x2={CHART_WIDTH - CHART_PAD_RIGHT} y1={dryY} y2={dryY} className="stroke-brand-mint" strokeWidth={1.5} strokeDasharray="5 4" />
          <text x={CHART_WIDTH - CHART_PAD_RIGHT} y={dryY - 4} textAnchor="end" className="fill-ink text-[8px] font-bold">
            constant mass
          </text>
        </g>
      ) : null}
      {Array.from({ length: MAX_WEIGHINGS }, (_, index) => {
        const x = CHART_PAD_LEFT + slot * index + (slot - barW) / 2;
        const mass = shown[index];
        return (
          <g key={index}>
            {mass !== undefined ? (
              <rect x={x} y={y(mass)} width={barW} height={baseY - y(mass)} rx={3} className="fill-brand-sky/70" />
            ) : (
              <rect x={x} y={baseY - 4} width={barW} height={4} rx={2} className="fill-hairline" />
            )}
            <text x={x + barW / 2} y={baseY + 12} textAnchor="middle" className="fill-slate2 text-[8px] font-bold">
              #{index + 1}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function MassBreakdown({ problem, model }: { problem: GravimetricProblem; model: GravimetricModel }) {
  const analytePercentOfPrecipitate = model.gravimetricFactor * 100;
  return (
    <div className="mt-4">
      <p className="text-sm font-extrabold text-ink">
        What part of the {formatMass(model.precipitateMassG)} g of {problem.weighedForm} is {problem.analyte}?
      </p>
      <div
        className="mt-2 flex h-8 w-full overflow-hidden rounded-full border border-hairline"
        role="img"
        aria-label={`${problem.analyte} is ${analytePercentOfPrecipitate.toFixed(1)}% of the precipitate mass (${formatMass(model.analyteMassG)} g)`}
      >
        <div className="flex items-center justify-center bg-brand-coral/70 text-xs font-extrabold text-ink" style={{ width: `${analytePercentOfPrecipitate}%` }}>
          {analytePercentOfPrecipitate >= 15 ? problem.analyte : ''}
        </div>
        <div className="flex flex-1 items-center justify-center bg-surface text-xs font-bold text-slate2">
          rest of {problem.weighedForm}
        </div>
      </div>
      <p className="mt-1 text-xs font-bold text-slate2">
        Gravimetric factor = {problem.analytePerWeighedForm} × {model.analyteMolarMass.toFixed(2)} ÷{' '}
        {model.weighedMolarMass.toFixed(2)} = {model.gravimetricFactor.toFixed(4)}
      </p>
    </div>
  );
}

function ErrorSourceRow({ source }: { source: GravimetricErrorSource }) {
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const predict = (effect: ErrorEffect) => {
    const result = checkErrorEffect(source, effect);
    setFeedback({ correct: result.correct, text: result.feedback });
  };
  return (
    <li className="rounded-2xl border border-hairline bg-white p-4">
      <p className="text-sm font-extrabold text-ink">{source.label}</p>
      <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label={`Effect of: ${source.label}`}>
        {(['high', 'low'] as const).map((effect) => (
          <button
            key={effect}
            type="button"
            onClick={() => predict(effect)}
            className="rounded-full border border-hairline px-3 py-1.5 text-xs font-extrabold text-ink-soft hover:bg-surface"
          >
            Result too {effect}
          </button>
        ))}
      </div>
      {feedback ? (
        <p className={`mt-2 text-sm font-medium leading-6 ${feedback.correct ? 'text-ink' : 'text-ink-soft'}`}>
          {feedback.correct ? '✓ ' : '✗ '}
          {feedback.text}
        </p>
      ) : null}
    </li>
  );
}

export function GravimetricMode() {
  const [problemId, setProblemId] = useState(GRAVIMETRIC_PROBLEMS[0].id);
  const [view, setView] = useState<'explore' | 'practice'>('explore');
  const [ratioFeedback, setRatioFeedback] = useState<Feedback | null>(null);
  const [count, setCount] = useState(0);
  const [answerText, setAnswerText] = useState('');
  useScienceTutorSection('GravimetricMode', { problemId, count, answerText });
  const [answerFeedback, setAnswerFeedback] = useState<Feedback | null>(null);

  const problem = GRAVIMETRIC_PROBLEMS.find((item) => item.id === problemId) ?? GRAVIMETRIC_PROBLEMS[0];
  const model = buildGravimetricModel(problem);
  const taken = model.weighings.slice(0, count);
  const constant = isConstantMass(taken);

  const chooseProblem = (id: string) => {
    setProblemId(id);
    setRatioFeedback(null);
    setCount(0);
    setAnswerText('');
    setAnswerFeedback(null);
  };

  const check = () => {
    const result = checkGravimetricAnswer(problem, count, Number(answerText));
    setAnswerFeedback({ correct: result.correct, text: result.feedback });
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[300px_minmax(0,1fr)]">
      <aside className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
          Gravimetric problems
        </p>
        <div className="mt-4 grid gap-2">
          {GRAVIMETRIC_PROBLEMS.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              aria-pressed={candidate.id === problem.id}
              onClick={() => chooseProblem(candidate.id)}
              className={`rounded-2xl border px-4 py-3 text-left transition-colors ${
                candidate.id === problem.id
                  ? 'border-brand-sky bg-wash-sky text-ink'
                  : 'border-hairline bg-white text-slate2 hover:border-brand-sky/40'
              }`}
            >
              <span className="block text-sm font-extrabold">{candidate.title}</span>
              <span className="block text-xs font-medium">
                {candidate.analyte} weighed as {candidate.weighedForm}
              </span>
            </button>
          ))}
        </div>
        <div className="mt-5 rounded-2xl bg-wash-sunshine p-4 text-sm font-medium leading-6 text-ink-soft">
          Gravimetric analysis: convert all of one species into a pure, insoluble solid of known
          formula, then weigh it. Mass → moles → mole ratio → mass of analyte.
        </div>
      </aside>

      <article className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
          {problem.analyteName} · weighed as {problem.weighedFormName}
        </p>
        <h2 className="mt-2 text-2xl font-extrabold text-ink">{problem.title}</h2>
        <p className="mt-2 text-sm font-medium leading-6 text-slate2">{problem.context}</p>
        <ul className="mt-3 grid gap-1" aria-label="Precipitation equations">
          {problem.equations.map((equation) => (
            <li key={equation} className="overflow-x-auto rounded-xl bg-surface px-3 py-2 font-mono text-sm font-bold text-ink">
              {equation}
            </li>
          ))}
        </ul>

        <div className="mt-4 grid max-w-xs grid-cols-2 rounded-full bg-surface p-1" aria-label="Explore or practise">
          {(['explore', 'practice'] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={view === option}
              onClick={() => setView(option)}
              className={`rounded-full px-3 py-2 text-sm font-extrabold ${view === option ? 'bg-ink text-white shadow-card-soft' : 'text-slate2 hover:bg-white'}`}
            >
              {option === 'explore' ? 'Explore' : 'Practice'}
            </button>
          ))}
        </div>

        {view === 'explore' ? <InstrumentalGravimetricExplore key={problem.id} problem={problem} /> : null}

        {view === 'practice' ? (
          <>
        <section className="mt-6" aria-labelledby="ratio-heading">
          <h3 id="ratio-heading" className="text-lg font-extrabold text-ink">
            1 · Predict the mole ratio
          </h3>
          <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Mole ratio options">
            {RATIO_OPTIONS.map((ratio) => (
              <button
                key={ratio}
                type="button"
                onClick={() => {
                  const result = checkRatioPrediction(problem, ratio);
                  setRatioFeedback({ correct: result.correct, text: result.feedback });
                }}
                className="rounded-full border border-hairline bg-white px-4 py-2 font-mono text-sm font-bold text-ink hover:bg-surface"
              >
                {ratioLabel(problem, ratio)}
              </button>
            ))}
          </div>
          {ratioFeedback ? (
            <FeedbackPanel feedback={ratioFeedback} label="Mole ratio feedback" correctTitle="Correct ratio" wrongTitle="Check the formula" />
          ) : null}
        </section>

        <section className="mt-6" aria-labelledby="weigh-heading">
          <h3 id="weigh-heading" className="text-lg font-extrabold text-ink">
            2 · Dry to constant mass
          </h3>
          <p className="mt-1 text-sm font-medium leading-6 text-slate2">{problem.treatment}</p>
          <div className="mt-4 grid gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
            <div className="min-w-0">
              <table className="w-full text-left text-sm">
                <caption className="mb-2 text-left text-sm font-extrabold text-ink">
                  Empty crucible: {formatMass(problem.emptyCrucibleG)} g
                </caption>
                <thead>
                  <tr className="text-xs font-extrabold uppercase tracking-[0.1em] text-slate2">
                    <th className="py-1">Weighing</th>
                    <th className="py-1">Crucible + solid (g)</th>
                    <th className="py-1">Change (g)</th>
                  </tr>
                </thead>
                <tbody>
                  {taken.map((mass, index) => (
                    <tr key={index} className="border-t border-hairline">
                      <td className="py-2 font-bold text-ink">#{index + 1}</td>
                      <td className="py-2 font-mono font-bold text-ink">{formatMass(mass)}</td>
                      <td className="py-2 font-mono text-slate2">
                        {index === 0 ? '—' : `−${formatMass(taken[index - 1] - mass)}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button
                type="button"
                disabled={constant || count >= MAX_WEIGHINGS}
                onClick={() => {
                  setCount((current) => current + 1);
                  setAnswerFeedback(null);
                }}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Flame className="h-4 w-4" aria-hidden="true" /> Heat, cool and weigh
              </button>
              <p className="mt-2 text-xs font-bold leading-5 text-slate2" aria-live="polite">
                {count === 0
                  ? 'No weighing yet.'
                  : constant
                    ? `Constant mass reached: the last two weighings agree within ${CONSTANT_MASS_TOLERANCE_G} g, so all the water has gone.`
                    : `Mass is still falling as water leaves. Repeat until two weighings agree within ${CONSTANT_MASS_TOLERANCE_G} g.`}
              </p>
            </div>
            <WeighingChart problem={problem} model={model} count={count} />
          </div>
        </section>

        {count > 0 ? (
          <section className="mt-6 rounded-2xl border border-hairline bg-surface p-4" aria-labelledby="percent-heading">
            <h3 id="percent-heading" className="flex items-center gap-2 text-lg font-extrabold text-ink">
              <Scale className="h-5 w-5 text-brand-sky" aria-hidden="true" /> 3 · Calculate % {problem.analyte}
            </h3>
            <p className="mt-1 text-sm font-bold text-ink-soft">
              Sample mass {formatMass(problem.sampleMassG)} g · M({problem.weighedForm}) ={' '}
              {model.weighedMolarMass.toFixed(2)} g mol⁻¹ · M({problem.analyte}) ={' '}
              {model.analyteMolarMass.toFixed(2)} g mol⁻¹
            </p>
            <label htmlFor="gravimetric-answer" className="mt-3 block text-sm font-extrabold text-ink">
              % {problem.analyte} by mass in the sample
            </label>
            <div className="mt-2 flex flex-col gap-3 sm:flex-row">
              <input
                id="gravimetric-answer"
                type="number"
                inputMode="decimal"
                min={0}
                step="any"
                value={answerText}
                onChange={(event) => setAnswerText(event.target.value)}
                className="w-full max-w-[12rem] rounded-xl border border-hairline bg-white px-3 py-2 text-sm font-bold text-ink"
              />
              <button
                type="button"
                disabled={answerText.trim() === ''}
                onClick={check}
                className="rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                Check percentage
              </button>
            </div>
            {answerFeedback ? (
              <FeedbackPanel feedback={answerFeedback} label="Percentage feedback" correctTitle={`Correct: ${formatPercent(model.percent)}%`} wrongTitle="Check the calculation" />
            ) : null}
            {answerFeedback?.correct ? <MassBreakdown problem={problem} model={model} /> : null}
          </section>
        ) : null}
          </>
        ) : null}

        <section className="mt-6" aria-labelledby="errors-heading">
          <h3 id="errors-heading" className="text-lg font-extrabold text-ink">
            4 · Sources of error: high or low?
          </h3>
          <ul className="mt-3 grid gap-3">
            {problem.errorSources.map((source) => (
              <ErrorSourceRow key={`${problem.id}-${source.id}`} source={source} />
            ))}
          </ul>
        </section>
      </article>
    </div>
  );
}
